import qs from 'qs';
import isBot from 'isbot';
import preserveRedirectRouteQuery from './PreserveRedirectRouteQuery.gql';
import ProductPathnameRedirectQuery from './ProductPathnameRedirectQuery.gql';
import CategoryPathnameRedirectQuery from './CategoryPathnameRedirectQuery.gql';
import StartPagePathnameRedirectQuery from './StartPagePathnameRedirectQuery.gql';
import PagePathnameRedirectQuery from './PagePathnameRedirectQuery.gql';
import { setChannel } from '../../boot/apollo';
const PathnameRedirectQueryMap = {
    Product: ProductPathnameRedirectQuery,
    Category: CategoryPathnameRedirectQuery,
    StartPage: StartPagePathnameRedirectQuery,
    Page: PagePathnameRedirectQuery
};
const getURLBasename = (url) => {
    return url.pathname.split('/')[1];
};
/**
 * Returns the default channel from a list of channels
 * @param channels List of channels to search through
 */
export const getDefaultChannel = (channels) => {
    return channels.find((channel) => channel.isDefault);
};
/**
 * Converts a Channel object to a SelectedChannel with the specified culture and currency
 * @param channel Channel to convert
 * @param culture Desired culture
 * @param currency Desired currency
 * @param country? Optional desired country code
 */
export const createSelectedChannel = (channel, culture, currency, country) => {
    const selectedLanguage = channel.languages.find(language => language.culture === culture);
    const selectedCurrency = channel.currencies.find(c => c.id === currency);
    // Set selectedCountry to the prop `country`, else use the default
    const selectedCountry = channel.countries.find(c => c.code === country) ||
        channel.countries.find(c => c.isDefault);
    const selectedCountrySettings = channel.settings.countrySettings.find(countrySetting => {
        return countrySetting.countryCode === selectedCountry.code;
    });
    return Object.assign({}, channel, { currency: selectedCurrency || channel.defaultCurrency, language: selectedLanguage || channel.defaultLanguage, country: selectedCountry, countrySettings: selectedCountrySettings });
};
/**
 * Returns a channel with the specified name
 * @param channelName Name of the channel to be fond
 * @param channels Array of channels to search through
 */
export const findChannel = (channelName, channels) => {
    return channels.find(channel => channel.name === channelName);
};
const sanitizeURL = (url) => String(url).replace(/^(?:[\w]+:\/\/)?(?:www\.)?/, '');
export const findChannelByUrl = (url, channels, singleDomainMode) => {
    if (singleDomainMode) {
        const basename = getURLBasename(new URL(url));
        return channels.find(channel => basename === getURLBasename(new URL(channel.url)));
    }
    else {
        const currentUrl = sanitizeURL(url);
        const sorted = channels.slice().sort((a, b) => b.url.length - a.url.length);
        return sorted.find(channel => currentUrl.indexOf(sanitizeURL(channel.url)) === 0);
    }
};
export const findChannelById = (id, channels) => {
    return channels.find(channel => channel.id === id);
};
export const findChannelByCountry = (countryCode, channels) => {
    return channels.find(channel => channel.countries.find(country => country.code === countryCode)
        ? true
        : false);
};
const getRedirectUrl = (fullUrl, redirectChannel, geoipCountry, singleDomainMode) => {
    const { pathname } = new URL(fullUrl);
    if (singleDomainMode) {
        let urlArray = pathname.split('/');
        urlArray.splice(0, 2);
        const pathWithoutBasename = urlArray.join('/');
        return (redirectChannel.url +
            '/' +
            pathWithoutBasename +
            '?channelCountry=' +
            geoipCountry);
    }
    return redirectChannel.url + pathname + '?channelCountry=' + geoipCountry;
};
const getQueryObject = (url) => {
    return qs.parse(new URL(url).search, { ignoreQueryPrefix: true });
};
export const getGeoRedirect = ({ fullUrl, geoipCountry, channels, countryFromCookie, selectedChannel, userAgent, disableGeoRedirect, singleDomainMode }) => {
    // Opt our if disabled or we do not have channel data yet
    const currentChannel = findChannelByUrl(fullUrl.split(/\?|\#/)[0], channels, singleDomainMode);
    if (!currentChannel || disableGeoRedirect || isBot(userAgent)) {
        return null;
    }
    // Opt out if we have already set a country from the cookie or query string
    const { channelCountry, redirectDisabled } = getQueryObject(fullUrl);
    if (channelCountry || countryFromCookie || redirectDisabled) {
        return null;
    }
    // Redirect to the correct channel if we have a GeoIP match, and we're not
    // already in that channel
    const redirectChannel = findChannelByCountry(geoipCountry, channels);
    if (redirectChannel && selectedChannel.country.code !== geoipCountry) {
        return getRedirectUrl(fullUrl, redirectChannel, geoipCountry, singleDomainMode);
    }
    else
        return null;
};
const setChannelCookies = ({ culture, currency, channelCountry, channel, redirectDisabled }, cookies) => {
    let expires = new Date();
    expires.setFullYear(new Date().getFullYear() + 10);
    if (redirectDisabled) {
        cookies.remove('culture');
        cookies.remove('currency');
        cookies.remove('country');
        cookies.remove('channel');
    }
    culture && cookies.set('culture', culture, { expires, path: '/' });
    currency && cookies.set('currency', currency, { expires, path: '/' });
    channelCountry &&
        cookies.set('country', channelCountry, { expires, path: '/' });
    channel && cookies.set('channel', channel, { expires, path: '/' });
};
export const buildSelectedChannel = ({ url, cookies, channels, singleDomainMode }) => {
    // Get the culture, currency, country, channel from query string if set
    const queryStringParams = getQueryObject(url);
    // Set those values in to cookies
    setChannelCookies(queryStringParams, cookies);
    // Check if there is a valid channel derived from the pathname
    const cultureFromCookie = cookies.get('culture');
    const currencyFromCookie = cookies.get('currency');
    const countryFromCookie = cookies.get('country');
    const channelNameFromCookie = cookies.get('channel');
    const channelFromCookie = findChannel(channelNameFromCookie, channels);
    // If there is not a valid channel in the query string, but there is one in
    // the url, select it.
    if (channelFromCookie) {
        return createSelectedChannel(channelFromCookie, cultureFromCookie, currencyFromCookie, countryFromCookie);
        // If there's a valid channel derived from the cookies we just set (or that
        // have already been set), select that
    }
    const channelFromUrl = findChannelByUrl(url, channels, singleDomainMode);
    if (channelFromUrl) {
        return createSelectedChannel(channelFromUrl, cultureFromCookie, currencyFromCookie, countryFromCookie);
    }
    // Otherwise, use the default channel
    return createSelectedChannel(getDefaultChannel(channels));
};
export const updateChannel = ({ newChannel, config, client, basename }) => {
    let channel = newChannel;
    // If no country is set, select the default country
    if (!newChannel.country) {
        channel = Object.assign({}, newChannel, { country: newChannel.countries.find(c => c.isDefault) });
    }
    const inDev = window.location.hostname.indexOf('localhost') >= 0 ||
        window.location.hostname.indexOf('.dev.jetshop.se') >= 0 ||
        process.env.NODE_ENV === 'development';
    let baseUrl = channel.url;
    // Used for changing channel in development
    if (inDev) {
        // First check if the channel has a basename
        const hasBasename = inDev && getURLBasename(new URL(channel.url));
        // If the channel has a basename we prepend it with a slash
        const devBasename = hasBasename ? '/' + hasBasename : '';
        baseUrl = window.location.origin + devBasename;
    }
    function redirect(path) {
        const newUrl = `${path}${qs.stringify({
            culture: channel.language.culture,
            currency: channel.currency.id,
            channelCountry: channel.country.code,
            channel: channel.name
        }, { addQueryPrefix: true })}`;
        window.location.href = newUrl;
    }
    //Unless they specifically disable redirect we want to preserve the route
    if (config.preserveRedirect !== false) {
        let path = window.location.pathname.replace(basename, '');
        client
            .query({
            query: preserveRedirectRouteQuery,
            variables: {
                path
            }
        })
            .then(routeQueryResult => {
            const { id, __typename } = routeQueryResult.data.route.object;
            setChannel(newChannel);
            return client
                .query({
                fetchPolicy: 'network-only',
                query: PathnameRedirectQueryMap[__typename],
                variables: {
                    id
                }
            })
                .then(pathnameQueryResult => {
                let newPath = '';
                newPath = pathnameQueryResult.data[__typename].primaryRoute.path;
                redirect(`${baseUrl}${newPath}`);
            });
        })
            .catch(() => {
            redirect(baseUrl);
        });
    }
    else {
        redirect(baseUrl);
    }
};
export const singleDomainRedirect = ({ url, selectedChannel, channels }) => {
    //Get the basename of the current url
    let redirectUrl = new URL(url);
    const basename = getURLBasename(redirectUrl);
    //Find the current channel associated with that basename
    const currentChannelFromBasename = channels.find(channel => {
        const channelUrlBasename = getURLBasename(new URL(channel.url));
        return channelUrlBasename === basename;
    });
    const { redirectDisabled } = getQueryObject(url);
    //If the current channel from basename and selectedChannel are the same no redirect is needed
    if (redirectDisabled ||
        (currentChannelFromBasename &&
            currentChannelFromBasename.name === selectedChannel.name)) {
        return;
    }
    //Get the basename of the selected channel
    const channelUrl = new URL(selectedChannel.url);
    const channelUrlBasename = getURLBasename(channelUrl);
    if (currentChannelFromBasename) {
        // Swap it out with the old basename
        redirectUrl.pathname = redirectUrl.pathname.replace(basename, channelUrlBasename);
        return redirectUrl.toString();
    }
    else {
        redirectUrl.pathname = channelUrlBasename + redirectUrl.pathname;
        return redirectUrl.toString();
    }
};
//# sourceMappingURL=utils.js.map